/**
 * @fileoverview Advanced form component for creating and editing rooms
 * @author Hospital Room Scheduler Team
 * @created 2025-01-08
 */

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  Switch,
  Group,
  Button,
  Text,
  LoadingOverlay,
  Divider,
  Paper,
  Title,
  MultiSelect,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconBuilding, IconDoor, IconInfoCircle } from '@tabler/icons-react';
import { useRoomById, useUpdateRoom } from '../../hooks/admin/useRooms';
import { useBuildings } from '../../hooks/admin/useBuildings';
import type { CreateRoomData, UpdateRoomData } from '../../api/endpoints/rooms';
import { getFloorsForBuilding } from '../../api/endpoints/rooms';

/**
 * Interface for room form data
 * Modified: 07-10-2025 14:01:32 - Added allowedSpecialties and defaultBuffer (Gaps G3-3, G3-4)
 */
interface RoomFormData {
  number: string;
  name: string;
  floorId: string;
  capacity: number;
  description: string;
  isActive: boolean;
  allowedSpecialties: string[]; // Gap G3-3: Multi-select dropdown
  defaultBuffer: number; // Gap G3-4: Buffer minutes
}

/**
 * Props for RoomForm component
 */
interface RoomFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRoomData) => Promise<void>;
  loading?: boolean;
  roomId?: string; // For edit mode
}

/**
 * Advanced room form component with floor selection and validation
 * Supports both create and edit modes with comprehensive validation
 */
export const RoomForm: React.FC<RoomFormProps> = ({
  opened,
  onClose,
  onSubmit,
  loading = false,
  roomId
}) => {
  const isEditMode = !!roomId;
  
  // State for floors based on selected building
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [availableFloors, setAvailableFloors] = useState<any[]>([]);

  // Data fetching
  const { data: roomData, isLoading: roomLoading } = useRoomById(roomId || '', isEditMode);
  const { data: buildingsData } = useBuildings();
  const updateRoomMutation = useUpdateRoom();

  // Form setup
  const form = useForm<RoomFormData>({
    initialValues: {
      number: '',
      name: '',
      floorId: '',
      capacity: 1,
      description: '',
      isActive: true,
      allowedSpecialties: [], // Gap G3-3
      defaultBuffer: 15, // Gap G3-4: Default 15 minutes
    },
    validate: {
      number: (value) => {
        if (!value.trim()) return 'Room number is required';
        if (value.length > 20) return 'Room number cannot exceed 20 characters';
        return null;
      },
      name: (value) => {
        if (!value.trim()) return 'Room name is required';
        if (value.length > 100) return 'Room name cannot exceed 100 characters';
        return null;
      },
      floorId: (value) => {
        if (!value) return 'Floor is required';
        return null;
      },
      capacity: (value) => {
        if (value < 1) return 'Capacity must be at least 1';
        if (value > 100) return 'Capacity cannot exceed 100';
        return null;
      },
      description: (value) => {
        if (value && value.length > 500) return 'Description cannot exceed 500 characters';
        return null;
      }
    }
  });

  // Update floors when building changes
  useEffect(() => {
    if (selectedBuildingId && buildingsData?.buildings) {
      const selectedBuilding = buildingsData.buildings.find(b => b.id === selectedBuildingId);
      if (selectedBuilding) {
        // Fetch real floors from API
        fetchFloorsForBuilding(selectedBuildingId);
      }
    } else {
      setAvailableFloors([]);
      form.setFieldValue('floorId', '');
    }
  }, [selectedBuildingId, buildingsData]);

  // Function to fetch floors for a building
  const fetchFloorsForBuilding = async (buildingId: string) => {
    try {
      const floors = await getFloorsForBuilding(buildingId);
      const floorOptions = floors.map((floor) => ({
        value: floor.id,
        label: floor.name
      }));
      setAvailableFloors(floorOptions);
    } catch (error) {
      console.error('Error fetching floors:', error);
      // Fallback to empty array
      setAvailableFloors([]);
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && roomData) {
      const buildingId = roomData.floor.buildingId;
      setSelectedBuildingId(buildingId);
      
      form.setValues({
        number: roomData.number,
        name: roomData.name,
        floorId: roomData.floorId,
        capacity: roomData.capacity,
        description: roomData.description || '',
        isActive: roomData.isActive
      });
    }
  }, [isEditMode, roomData]);

  /**
   * Handle form submission
   * @param values - Form values
   */
  const handleSubmit = async (values: RoomFormData) => {
    try {
      if (isEditMode && roomId) {
        // Update existing room
        const updateData: UpdateRoomData = {
          number: values.number,
          name: values.name,
          floorId: values.floorId,
          capacity: values.capacity,
          isActive: values.isActive,
          allowedSpecialties: values.allowedSpecialties, // Gap G3-3
          defaultBuffer: values.defaultBuffer, // Gap G3-4
        };
        
        if (values.description) {
          updateData.description = values.description;
        }
        
        await updateRoomMutation.mutateAsync({ id: roomId, data: updateData });
        onClose();
      } else {
        // Create new room
        const createData: CreateRoomData = {
          number: values.number,
          name: values.name,
          floorId: values.floorId,
          capacity: values.capacity,
          allowedSpecialties: values.allowedSpecialties, // Gap G3-3
          defaultBuffer: values.defaultBuffer, // Gap G3-4
        };
        
        if (values.description) {
          createData.description = values.description;
        }
        
        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  /**
   * Handle building selection change
   * @param buildingId - Selected building ID
   */
  const handleBuildingChange = (buildingId: string) => {
    setSelectedBuildingId(buildingId);
  };

  /**
   * Reset form when modal closes
   */
  const handleClose = () => {
    form.reset();
    setSelectedBuildingId('');
    setAvailableFloors([]);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm">
          <IconDoor size="1.25rem" />
          <Title order={4} fw={600}>
            {isEditMode ? 'Edit Room' : 'Create New Room'}
          </Title>
        </Group>
      }
      size="lg"
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <LoadingOverlay visible={loading || roomLoading} />
      
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          {/* Building Selection */}
          <Paper p="md" radius="md" withBorder>
            <Group gap="sm" mb="sm">
              <IconBuilding size="1rem" />
              <Text fw={500} size="sm">
                Location
              </Text>
            </Group>
            
            <Stack gap="md">
              <Select
                label="Building"
                placeholder="Select a building"
                data={buildingsData?.buildings?.map(building => ({
                  value: building.id,
                  label: building.name
                })) || []}
                value={selectedBuildingId}
                onChange={(value) => handleBuildingChange(value || '')}
                required
                disabled={isEditMode} // Don't allow changing building in edit mode
                {...(isEditMode && {
                  value: selectedBuildingId,
                  readOnly: true
                })}
              />
              
              <Select
                label="Floor"
                placeholder={
                  !selectedBuildingId 
                    ? "Select a building first" 
                    : availableFloors.length === 0 
                      ? "This building has no floors" 
                      : "Select a floor"
                }
                data={availableFloors}
                {...form.getInputProps('floorId')}
                required
                disabled={!selectedBuildingId || availableFloors.length === 0}
                nothingFoundMessage={
                  !selectedBuildingId 
                    ? "Please select a building first" 
                    : "No floors available for this building"
                }
              />
              
              {/* Help text for empty floors */}
              {selectedBuildingId && availableFloors.length === 0 && (
                <Text size="xs" c="orange" mt={-8}>
                  <IconInfoCircle size={12} style={{ marginRight: 4, verticalAlign: 'text-top' }} />
                  This building does not have any floors defined. Please{' '}
                  <Text
                    component="a"
                    href="/admin/buildings"
                    target="_blank"
                    rel="noopener noreferrer"
                    c="blue"
                    fw={500}
                    style={{ textDecoration: 'underline' }}
                  >
                    create floors for this building
                  </Text>
                  {' '}first.
                </Text>
              )}
            </Stack>
          </Paper>

          {/* Room Details */}
          <Paper p="md" radius="md" withBorder>
            <Text fw={500} size="sm" mb="md">
              Room Information
            </Text>
            
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Room Number"
                  placeholder="e.g., A101, B205"
                  {...form.getInputProps('number')}
                  required
                />
                
                <TextInput
                  label="Room Name"
                  placeholder="e.g., Operating Room 1"
                  {...form.getInputProps('name')}
                  required
                />
              </Group>
              
              <Group grow>
                <NumberInput
                  label="Capacity"
                  placeholder="Maximum number of people"
                  min={1}
                  max={100}
                  {...form.getInputProps('capacity')}
                  required
                />
                
                <div>
                  <Text size="sm" fw={500} mb="xs">
                    Status
                  </Text>
                  <Switch
                    label="Active"
                    description="Room is available for scheduling"
                    {...form.getInputProps('isActive', { type: 'checkbox' })}
                  />
                </div>
              </Group>
              
              <Textarea
                label="Description"
                placeholder="Optional description of the room..."
                minRows={3}
                maxRows={5}
                {...form.getInputProps('description')}
              />
              
              <MultiSelect
                label="Allowed Specialties"
                placeholder="Select allowed medical specialties"
                description="Which specialties can use this room"
                data={[
                  { value: 'Cardiologist', label: 'Cardiologist' },
                  { value: 'Orthopedic Surgeon', label: 'Orthopedic Surgeon' },
                  { value: 'General Surgeon', label: 'General Surgeon' },
                  { value: 'Pediatrician', label: 'Pediatrician' },
                  { value: 'Neurologist', label: 'Neurologist' },
                  { value: 'Dermatologist', label: 'Dermatologist' },
                  { value: 'Oncologist', label: 'Oncologist' },
                  { value: 'Radiologist', label: 'Radiologist' },
                ]}
                {...form.getInputProps('allowedSpecialties')}
                searchable
                clearable
              />
              
              <NumberInput
                label="Default Buffer (Minutes)"
                placeholder="Buffer time between sessions"
                description="Time allocated for cleaning/preparation between sessions"
                min={0}
                max={60}
                step={5}
                {...form.getInputProps('defaultBuffer')}
              />
            </Stack>
          </Paper>

          {/* Form Actions */}
          <Divider />
          
          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              loading={loading || updateRoomMutation.isPending}
              leftSection={!isEditMode && <IconDoor size="1rem" />}
            >
              {isEditMode ? 'Update Room' : 'Create Room'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
